import React, { useEffect, useState, useMemo } from 'react';
import {
    Users,
    MapPin,
    Search,
    ChevronRight,
    Vote,
    Grid,
    List,
    UserCheck,
    Globe,
    CheckCircle,
    Home,
    Landmark
} from 'lucide-react';

import { ElectionsSEO } from '../components/SEO/ElectionSEO';

// Import des données des candidats (structure corrigée)
import candidatsData from '../constants/candidats_udb_legislatives.json';

// Types pour TypeScript
type ProvinceData = {
    code: string;
    communes?: {
        [key: string]: {
            type: string;
            arrondissements: {
                nom: string;
                sieges: {
                    siege: string;
                    candidat: string;
                    suppleant: string;
                }[];
            }[];
        };
    };
    departements?: {
        [key: string]: {
            type: string;
            cantons: {
                nom: string;
                sieges: {
                    siege: string;
                    candidat: string;
                    suppleant: string;
                }[];
            }[];
        };
    };
};

type CandidatsData = {
    election: {
        titre: string;
        parti: string;
        date_rapport: string;
        president_commission: string;
    };
    provinces: {
        [key: string]: ProvinceData;
    };
    diaspora: {
        zones: {
            nom: string;
            siege: string;
            candidat: string;
            suppleant: string;
        }[];
    };
};

const ElectionsLegislativesPage: React.FC = () => {
    // Cast des données pour TypeScript
    const typedCandidatsData = candidatsData as CandidatsData;
    const [selectedProvince, setSelectedProvince] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [expandedProvinces, setExpandedProvinces] = useState<Set<string>>(new Set());
    const [expandedCommunes, setExpandedCommunes] = useState<Set<string>>(new Set());
    const [expandedDepartements, setExpandedDepartements] = useState<Set<string>>(new Set());
    const [visibleProvinces, setVisibleProvinces] = useState<Set<string>>(new Set());

    // Seulement 9 provinces + diaspora avec mapping des images
    const provinces = ['Estuaire', 'Haut-Ogooué', 'Moyen-Ogooué', 'Ngounié', 'Nyanga', 'Ogooué-Ivindo', 'Ogooué-Lolo', 'Ogooué-Maritime', 'Woleu-Ntem'];

    const getProvinceImageName = (provinceName: string): string => {
        const imageMap: { [key: string]: string } = {
            'Estuaire': 'G1.jpg',
            'Haut-Ogooué': 'G2.jpg',
            'Moyen-Ogooué': 'G3.jpg',
            'Ngounié': 'G4.jpg',
            'Nyanga': 'G5.jpg',
            'Ogooué-Ivindo': 'G6.jpg',
            'Ogooué-Lolo': 'G7.jpg',
            'Ogooué-Maritime': 'G8.jpg',
            'Woleu-Ntem': 'G9.jpg'
        };
        return imageMap[provinceName] || 'default.jpg';
    };

    useEffect(() => {
        window.scrollTo(0, 0);

        const timer = setTimeout(() => {
            provinces.forEach((province, index) => {
                setTimeout(() => {
                    setVisibleProvinces(prev => {
                        const newSet = new Set(prev);
                        newSet.add(province);
                        return newSet;
                    });
                }, index * 150);
            });
        }, 300);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Filtrage des données avec recherche fonctionnelle
    const filteredData = useMemo(() => {
        const filtered: { [key: string]: ProvinceData } = {};

        provinces.forEach(provinceName => {
            if (selectedProvince && selectedProvince !== provinceName) return;

            const provinceData = typedCandidatsData.provinces[provinceName] as ProvinceData;
            if (!provinceData) return;

            const filteredProvince: ProvinceData = {
                code: provinceData.code,
                communes: {},
                departements: {}
            };

            let hasMatches = false;

            // Filtrer les communes
            if (provinceData.communes) {
                Object.entries(provinceData.communes).forEach(([communeName, commune]) => {
                    const filteredCommune = {
                        ...commune,
                        arrondissements: commune.arrondissements?.filter((arr: any) => {
                            if (!searchTerm.trim()) return true;

                            const searchLower = searchTerm.toLowerCase();
                            return (
                                provinceName.toLowerCase().includes(searchLower) ||
                                communeName.toLowerCase().includes(searchLower) ||
                                arr.nom.toLowerCase().includes(searchLower) ||
                                arr.sieges?.some((siege: any) =>
                                    siege.candidat.toLowerCase().includes(searchLower) ||
                                    siege.suppleant.toLowerCase().includes(searchLower) ||
                                    siege.siege.toLowerCase().includes(searchLower)
                                )
                            );
                        }).map((arr: any) => ({
                            ...arr,
                            sieges: !searchTerm.trim() ? arr.sieges : arr.sieges?.filter((siege: any) => {
                                const searchLower = searchTerm.toLowerCase();
                                return (
                                    provinceName.toLowerCase().includes(searchLower) ||
                                    communeName.toLowerCase().includes(searchLower) ||
                                    arr.nom.toLowerCase().includes(searchLower) ||
                                    siege.candidat.toLowerCase().includes(searchLower) ||
                                    siege.suppleant.toLowerCase().includes(searchLower) ||
                                    siege.siege.toLowerCase().includes(searchLower)
                                );
                            })
                        }))
                    };

                    if (filteredCommune.arrondissements && filteredCommune.arrondissements.length > 0) {
                        filteredProvince.communes![communeName] = filteredCommune;
                        hasMatches = true;
                    }
                });
            }

            // Filtrer les départements
            if (provinceData.departements) {
                Object.entries(provinceData.departements).forEach(([departementName, departement]) => {
                    const filteredDepartement = {
                        ...departement,
                        cantons: departement.cantons?.filter((canton: any) => {
                            if (!searchTerm.trim()) return true;

                            const searchLower = searchTerm.toLowerCase();
                            return (
                                provinceName.toLowerCase().includes(searchLower) ||
                                departementName.toLowerCase().includes(searchLower) ||
                                canton.nom.toLowerCase().includes(searchLower) ||
                                canton.sieges?.some((siege: any) =>
                                    siege.candidat.toLowerCase().includes(searchLower) ||
                                    siege.suppleant.toLowerCase().includes(searchLower) ||
                                    siege.siege.toLowerCase().includes(searchLower)
                                )
                            );
                        }).map((canton: any) => ({
                            ...canton,
                            sieges: !searchTerm.trim() ? canton.sieges : canton.sieges?.filter((siege: any) => {
                                const searchLower = searchTerm.toLowerCase();
                                return (
                                    provinceName.toLowerCase().includes(searchLower) ||
                                    departementName.toLowerCase().includes(searchLower) ||
                                    canton.nom.toLowerCase().includes(searchLower) ||
                                    siege.candidat.toLowerCase().includes(searchLower) ||
                                    siege.suppleant.toLowerCase().includes(searchLower) ||
                                    siege.siege.toLowerCase().includes(searchLower)
                                );
                            })
                        }))
                    };

                    if (filteredDepartement.cantons && filteredDepartement.cantons.length > 0) {
                        filteredProvince.departements![departementName] = filteredDepartement;
                        hasMatches = true;
                    }
                });
            }

            if (hasMatches || !searchTerm.trim()) {
                filtered[provinceName] = filteredProvince;
            }
        });

        return filtered;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, selectedProvince]);

    // Calcul des statistiques
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const stats = useMemo(() => {
        let totalCandidats = 0;
        let totalWomen = 0;
        let totalCommunes = 0;
        let totalDepartements = 0;

        // Compter les candidats des provinces
        provinces.forEach(provinceName => {
            const provinceData = typedCandidatsData.provinces[provinceName] as ProvinceData;
            if (!provinceData) return;

            // Communes
            if (provinceData.communes) {
                Object.values(provinceData.communes).forEach((commune: any) => {
                    totalCommunes++;
                    commune.arrondissements?.forEach((arr: any) => {
                        arr.sieges?.forEach((siege: any) => {
                            totalCandidats++;
                            if (siege.candidat.toLowerCase().includes('épouse') ||
                                siege.candidat.toLowerCase().includes('née') ||
                                siege.candidat.match(/^(marie|chantal|édwige|huguette|aurélie|jeannette|brigitte|sandria|zenaba|justine)/i)) {
                                totalWomen++;
                            }
                        });
                    });
                });
            }

            // Départements
            if (provinceData.departements) {
                Object.values(provinceData.departements).forEach((departement: any) => {
                    totalDepartements++;
                    departement.cantons?.forEach((canton: any) => {
                        canton.sieges?.forEach((siege: any) => {
                            totalCandidats++;
                            if (siege.candidat.toLowerCase().includes('épouse') ||
                                siege.candidat.toLowerCase().includes('née') ||
                                siege.candidat.match(/^(marie|chantal|édwige|huguette|aurélie|jeannette|brigitte|sandria|zenaba|justine)/i)) {
                                totalWomen++;
                            }
                        });
                    });
                });
            }
        });

        // Ajouter la diaspora
        typedCandidatsData.diaspora.zones.forEach(() => {
            totalCandidats++;
        });

        const totalMen = totalCandidats - totalWomen;

        return {
            totalCandidats,
            totalWomen,
            totalMen,
            womenPercentage: Math.round((totalWomen / totalCandidats) * 100),
            menPercentage: Math.round((totalMen / totalCandidats) * 100),
            totalProvinces: provinces.length,
            totalCommunes,
            totalDepartements
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleProvince = (provinceName: string) => {
        setExpandedProvinces(prev => {
            const newSet = new Set(prev);
            if (newSet.has(provinceName)) {
                newSet.delete(provinceName);
            } else {
                newSet.add(provinceName);
            }
            return newSet;
        });
    };

    const toggleCommune = (communeId: string) => {
        setExpandedCommunes(prev => {
            const newSet = new Set(prev);
            if (newSet.has(communeId)) {
                newSet.delete(communeId);
            } else {
                newSet.add(communeId);
            }
            return newSet;
        });
    };

    const toggleDepartement = (departementId: string) => {
        setExpandedDepartements(prev => {
            const newSet = new Set(prev);
            if (newSet.has(departementId)) {
                newSet.delete(departementId);
            } else {
                newSet.add(departementId);
            }
            return newSet;
        });
    };

    const getProvinceColor = (index: number) => {
        const colors = [
            { bg: 'bg-primary-blue', text: 'text-primary-blue', border: 'border-primary-blue', light: 'bg-primary-blue/10' },
            { bg: 'bg-primary-green', text: 'text-primary-green', border: 'border-primary-green', light: 'bg-primary-green/10' }
        ];
        return colors[index % colors.length];
    };

    const renderCandidateCard = (siege: any, isListView: boolean = false) => (
        <div
            className={`bg-white border border-gray-300 p-5 hover:border-primary-blue hover:shadow-md transition-all duration-300 ${
                isListView ? 'flex items-center space-x-4' : ''
            }`}
        >
            <div className="flex-1">
                <div className="flex items-start justify-between mb-3">
                    <span className="px-3 py-1 text-xs font-semibold bg-primary-blue text-white rounded-full">
                        {siege.siege}
                    </span>
                </div>

                <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Candidat</span>
                            <p className="font-semibold text-gray-900 text-sm mt-1 leading-tight">{siege.candidat}</p>
                        </div>
                    </div>
                    <div className="flex items-start space-x-3">
                        <UserCheck className="w-5 h-5 text-primary-blue flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Suppléant</span>
                            <p className="font-semibold text-gray-900 text-sm mt-1 leading-tight">{siege.suppleant}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderCommune = (communeName: string, commune: any, provinceName: string, colors: any) => {
        const communeId = `${provinceName}-${communeName}`;
        const isExpanded = expandedCommunes.has(communeId);

        const totalSieges = commune.arrondissements?.reduce((sum: number, arr: any) =>
            sum + (arr.sieges?.length || 0), 0) || 0;

        return (
            <div key={communeName} className="bg-white border border-gray-200 mb-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300">
                <div
                    className="p-6 cursor-pointer hover:bg-gray-50 border-l-4 border-primary-green rounded-l-lg"
                    onClick={() => toggleCommune(communeId)}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-primary-green/10 border border-primary-green/20 rounded-lg flex items-center justify-center">
                                <Home className="w-6 h-6 text-primary-green" />
                            </div>
                            <div>
                                <h4 className="font-bold text-gray-900 text-lg">{communeName}</h4>
                                <p className="text-sm text-gray-600 font-medium">Commune • {totalSieges} sièges</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-4">
                            <div className="text-center">
                                <span className="text-primary-green font-bold text-2xl">{totalSieges}</span>
                                <p className="text-xs text-gray-500 font-medium">sièges</p>
                            </div>
                            <ChevronRight
                                className={`w-6 h-6 text-gray-400 transition-transform duration-300 ${
                                    isExpanded ? 'rotate-90' : ''
                                }`}
                            />
                        </div>
                    </div>
                </div>

                {isExpanded && (
                    <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50/50">
                        {commune.arrondissements?.map((arrondissement: any, arrIndex: number) => (
                            <div key={arrIndex} className="mt-6">
                                <h5 className="font-semibold text-gray-800 mb-4 flex items-center bg-white p-3 rounded-lg border border-gray-200">
                                    <MapPin className="w-5 h-5 mr-3 text-primary-blue" />
                                    {arrondissement.nom}
                                    <span className="ml-auto text-sm text-gray-500 font-normal">
                                        {arrondissement.sieges?.length || 0} sièges
                                    </span>
                                </h5>
                                <div className={`grid gap-4 ${
                                    viewMode === 'grid'
                                        ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                                        : 'grid-cols-1'
                                }`}>
                                    {arrondissement.sieges?.map((siege: any, siegeIndex: number) => (
                                        <div key={siegeIndex}>
                                            {renderCandidateCard(siege, viewMode === 'list')}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderDepartement = (departementName: string, departement: any, provinceName: string, colors: any) => {
        const departementId = `${provinceName}-${departementName}`;
        const isExpanded = expandedDepartements.has(departementId);

        const totalSieges = departement.cantons?.reduce((sum: number, canton: any) =>
            sum + (canton.sieges?.length || 0), 0) || 0;

        return (
            <div key={departementName} className="bg-white border border-gray-200 mb-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300">
                <div
                    className="p-6 cursor-pointer hover:bg-gray-50 border-l-4 border-primary-blue rounded-l-lg"
                    onClick={() => toggleDepartement(departementId)}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-primary-blue/10 border border-primary-blue/20 rounded-lg flex items-center justify-center">
                                <Landmark className="w-6 h-6 text-primary-blue" />
                            </div>
                            <div>
                                <h4 className="font-bold text-gray-900 text-lg">{departementName}</h4>
                                <p className="text-sm text-gray-600 font-medium">Département • {totalSieges} sièges</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-4">
                            <div className="text-center">
                                <span className="text-primary-blue font-bold text-2xl">{totalSieges}</span>
                                <p className="text-xs text-gray-500 font-medium">sièges</p>
                            </div>
                            <ChevronRight
                                className={`w-6 h-6 text-gray-400 transition-transform duration-300 ${
                                    isExpanded ? 'rotate-90' : ''
                                }`}
                            />
                        </div>
                    </div>
                </div>

                {isExpanded && (
                    <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50/50">
                        {departement.cantons?.map((canton: any, cantonIndex: number) => (
                            <div key={cantonIndex} className="mt-6">
                                <h5 className="font-semibold text-gray-800 mb-4 flex items-center bg-white p-3 rounded-lg border border-gray-200">
                                    <MapPin className="w-5 h-5 mr-3 text-primary-green" />
                                    {canton.nom}
                                    <span className="ml-auto text-sm text-gray-500 font-normal">
                                        {canton.sieges?.length || 0} sièges
                                    </span>
                                </h5>
                                <div className={`grid gap-4 ${
                                    viewMode === 'grid'
                                        ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                                        : 'grid-cols-1'
                                }`}>
                                    {canton.sieges?.map((siege: any, siegeIndex: number) => (
                                        <div key={siegeIndex}>
                                            {renderCandidateCard(siege, viewMode === 'list')}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <ElectionsSEO type="legislatives" />


        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white pt-16">
            {/* Hero section */}
            <div className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div
                        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                        style={{
                            backgroundImage: `url('/images/elections-bg.jpg')`,
                        }}
                    />
                    <div className="absolute inset-0 bg-primary-blue/80"></div>
                    <div className="absolute inset-0 bg-black/30"></div>
                </div>

                <div className="relative z-10 container mx-auto px-4 text-center text-white">
                    <div className="inline-flex items-center space-x-2 text-primary-yellow text-sm mb-6 bg-white/10 backdrop-blur-sm px-4 py-2 border border-white/20">
                        <Vote className="w-4 h-4"/>
                        <span className="font-medium">Élections Législatives 2025</span>
                    </div>

                    <h1 className="text-5xl md:text-6xl font-light text-white mb-6">
                        Nos candidats
                        <span className="block text-primary-yellow font-normal">aux Législatives</span>
                    </h1>

                    <div className="w-20 h-1 bg-primary-yellow mx-auto mb-8"></div>

                    <p className="text-xl text-white/90 font-light max-w-3xl mx-auto mb-12">
                        Découvrez les candidats de l'UDB qui portent notre vision
                        pour le Gabon nouveau dans les 9 provinces
                    </p>


                </div>
            </div>

            {/* Section principale */}
            <section className="py-16 relative overflow-hidden">
                <div className="container mx-auto px-4 relative z-10 pt-8">
                    <div className="max-w-7xl mx-auto">
                        {/* Navigation et filtres */}
                        <div className="mb-12 space-y-6">
                            {/* Navigation rapide par provinces */}
                            <div className="bg-white/95 backdrop-blur-sm p-6 border border-gray-300">
                                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                    <MapPin className="w-5 h-5 mr-2 text-primary-blue" />
                                    Navigation rapide par provinces
                                </h3>
                                <div className="grid grid-cols-3 md:grid-cols-9 gap-4">
                                    {provinces.map((province, index) => {
                                        const isSelected = selectedProvince === province;
                                        const colors = getProvinceColor(index);
                                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                        const provinceData = typedCandidatsData.provinces[province] as ProvinceData;
                                        const imageCode = getProvinceImageName(province).replace('.jpg', '');

                                        return (
                                            <button
                                                key={province}
                                                onClick={() => setSelectedProvince(isSelected ? null : province)}
                                                className={`group relative p-4 border-2 transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                                                    isSelected
                                                        ? `${colors.bg} text-white border-transparent shadow-lg scale-105`
                                                        : `bg-white ${colors.text} ${colors.border} hover:${colors.bg} hover:text-white hover:border-transparent`
                                                }`}
                                            >
                                                <div className="flex flex-col items-center space-y-2">
                                                    <div className={`w-14 h-14 rounded-full overflow-hidden border-2 transition-colors ${
                                                        isSelected ? 'border-white/30' : 'border-gray-300 group-hover:border-white/30'
                                                    }`}>
                                                        <img
                                                            src={`/images/provinces/${getProvinceImageName(province)}`}
                                                            alt={`Écusson ${province}`}
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => {
                                                                e.currentTarget.src = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#e5e7eb"/><text x="12" y="16" text-anchor="middle" font-size="10" fill="#374151">${imageCode}</text></svg>`)}`;
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="text-xs font-bold mb-1">{imageCode}</div>
                                                        <div className="text-xs leading-tight">{province}</div>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Recherche et filtres */}
                            <div className="flex flex-col lg:flex-row gap-6 items-center">
                                <div className="relative flex-1 max-w-lg">
                                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Rechercher un candidat, circonscription..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-12 pr-4 py-4 border border-gray-400 focus:outline-none focus:border-primary-blue bg-white/95 backdrop-blur-sm text-base"
                                    />
                                </div>

                                <div className="flex items-center space-x-4">
                                    {(selectedProvince || searchTerm) && (
                                        <button
                                            onClick={() => {
                                                setSelectedProvince(null);
                                                setSearchTerm('');
                                            }}
                                            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-400 hover:bg-gray-50 transition-colors"
                                        >
                                            Réinitialiser
                                        </button>
                                    )}

                                    <div className="flex border border-gray-400">
                                        <button
                                            onClick={() => setViewMode('grid')}
                                            className={`px-4 py-3 transition-colors ${
                                                viewMode === 'grid' ? 'bg-primary-blue text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
                                            }`}
                                        >
                                            <Grid className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => setViewMode('list')}
                                            className={`px-4 py-3 border-l border-gray-300 transition-colors ${
                                                viewMode === 'list' ? 'bg-primary-blue text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
                                            }`}
                                        >
                                            <List className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Contenu principal */}
                        <div className="space-y-8">
                            {Object.entries(filteredData).map(([provinceName, provinceData], provinceIndex) => {
                                const colors = getProvinceColor(provinceIndex);
                                const isVisible = visibleProvinces.has(provinceName);
                                const isExpanded = expandedProvinces.has(provinceName);

                                // Calculer le total des sièges
                                const totalSieges = (
                                    (provinceData.communes ? Object.values(provinceData.communes).reduce((sum: number, commune: any) =>
                                        sum + (commune.arrondissements?.reduce((arrSum: number, arr: any) =>
                                            arrSum + (arr.sieges?.length || 0), 0) || 0), 0) : 0) +
                                    (provinceData.departements ? Object.values(provinceData.departements).reduce((sum: number, dept: any) =>
                                        sum + (dept.cantons?.reduce((cantonSum: number, canton: any) =>
                                            cantonSum + (canton.sieges?.length || 0), 0) || 0), 0) : 0)
                                );

                                return (
                                    <div
                                        key={provinceName}
                                        className={`transition-all duration-700 ${
                                            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                                        }`}
                                    >
                                        {/* En-tête de la province */}
                                        <div
                                            className={`${colors.bg} text-white p-8 cursor-pointer hover:opacity-95 transition-all duration-300 rounded-t-lg shadow-lg`}
                                            onClick={() => toggleProvince(provinceName)}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-6">
                                                    <div className="w-20 h-20 bg-white/15 border-2 border-white/30 rounded-xl flex items-center justify-center overflow-hidden shadow-inner">
                                                        <img
                                                            src={`/images/provinces/${getProvinceImageName(provinceName)}`}
                                                            alt={`Écusson ${provinceName}`}
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => {
                                                                // Fallback vers l'icône MapPin si l'image ne charge pas
                                                                e.currentTarget.style.display = 'none';
                                                                const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                                                if (fallback) fallback.style.display = 'flex';
                                                            }}
                                                        />
                                                        {/* Fallback avec icône */}
                                                        <MapPin className="w-10 h-10 hidden" />
                                                    </div>
                                                    <div>
                                                        <h2 className="text-3xl font-bold mb-2">{provinceName}</h2>
                                                        <p className="text-white/90 text-lg font-medium">{totalSieges} sièges disponibles</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center space-x-6">
                                                    <div className="text-center bg-white/10 rounded-lg p-4">
                                                        <div className="text-4xl font-bold mb-1">{totalSieges}</div>
                                                        <div className="text-white/80 text-sm font-medium uppercase tracking-wide">sièges</div>
                                                    </div>
                                                    <ChevronRight
                                                        className={`w-7 h-7 transition-transform duration-300 ${
                                                            isExpanded ? 'rotate-90' : ''
                                                        }`}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Contenu de la province */}
                                        {isExpanded && (
                                            <div className="bg-white border-2 border-gray-200 border-t-0 p-8 rounded-b-lg shadow-lg">
                                                {/* Communes */}
                                                {provinceData.communes && Object.keys(provinceData.communes).length > 0 && (
                                                    <div className="mb-10">
                                                        <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center bg-gradient-to-r from-primary-green/10 to-transparent p-4 rounded-lg border-l-4 border-primary-green">
                                                            <Home className="w-6 h-6 mr-3 text-primary-green" />
                                                            Communes
                                                            <span className="ml-3 text-sm font-normal text-gray-600">
                                                                ({Object.keys(provinceData.communes).length})
                                                            </span>
                                                        </h3>
                                                        <div className="space-y-6">
                                                            {Object.entries(provinceData.communes).map(([communeName, commune]) =>
                                                                renderCommune(communeName, commune, provinceName, colors)
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Départements */}
                                                {provinceData.departements && Object.keys(provinceData.departements).length > 0 && (
                                                    <div>
                                                        <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center bg-gradient-to-r from-primary-blue/10 to-transparent p-4 rounded-lg border-l-4 border-primary-blue">
                                                            <Landmark className="w-6 h-6 mr-3 text-primary-blue" />
                                                            Départements
                                                            <span className="ml-3 text-sm font-normal text-gray-600">
                                                                ({Object.keys(provinceData.departements).length})
                                                            </span>
                                                        </h3>
                                                        <div className="space-y-6">
                                                            {Object.entries(provinceData.departements).map(([departementName, departement]) =>
                                                                renderDepartement(departementName, departement, provinceName, colors)
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Message si aucun résultat */}
                            {searchTerm && Object.keys(filteredData).length === 0 && (
                                <div className="text-center py-12">
                                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Search className="w-8 h-8 text-gray-400" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-800 mb-2">Aucun résultat trouvé</h3>
                                    <p className="text-gray-600 mb-4">
                                        Aucun candidat ou circonscription ne correspond à votre recherche "{searchTerm}"
                                    </p>
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        className="bg-primary-blue text-white px-6 py-2 font-semibold hover:bg-primary-blue/90 transition-colors"
                                    >
                                        Effacer la recherche
                                    </button>
                                </div>
                            )}

                            {/* Diaspora */}
                            <div className="bg-gradient-to-r from-primary-blue to-primary-green text-white p-6">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                        <div className="w-16 h-16 bg-white/10 border border-white/20 flex items-center justify-center">
                                            <Globe className="w-8 h-8" />
                                        </div>
                                        <div>
                                            <h2 className="text-2xl font-bold mb-1">Diaspora</h2>
                                            <p className="text-white/90">{typedCandidatsData.diaspora.zones.length} zones</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {typedCandidatsData.diaspora.zones.map((zone, index) => (
                                        <div key={index} className="bg-white/10 backdrop-blur-sm p-4 border border-white/20">
                                            <h4 className="font-semibold mb-2">{zone.nom}</h4>
                                            <div className="space-y-1 text-sm">
                                                <p><span className="text-white/70">Candidat:</span> {zone.candidat}</p>
                                                <p><span className="text-white/70">Suppléant:</span> {zone.suppleant}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>



                        {/* Call to Action */}
                        <div className="mt-16 bg-gradient-to-r from-primary-blue to-primary-green text-white p-8 text-center">
                            <h3 className="text-2xl font-bold mb-4">Mobilisons-nous pour la Victoire !</h3>
                            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
                                Soutenez nos candidats et participez à la construction du Gabon nouveau.
                                Votre voix compte pour l'avenir de notre nation.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">

                                <a href="/adhesion"
                                   className="bg-primary-yellow text-gray-900 px-8 py-3 font-semibold hover:bg-primary-yellow/90 transition-colors inline-flex items-center space-x-2">
                                    <Users className="w-5 h-5"/>
                                    <span>Rejoindre le mouvement</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
        </>
    );
};

export default ElectionsLegislativesPage;
